import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useMemo } from 'react';

import { DataGridStateManagement } from '@/hooks/useDataGridState';
import { GetRunsQueryParams } from '@/types/schema';

import { SearchModel } from '../types';
import { parseRelativeDate } from '../utils/getDateTimeRangeLabel';

dayjs.extend(utc);
interface DebouncedFilterProps {
  runsFilter: GetRunsQueryParams | null;
  timeModel: DataGridStateManagement['timeModel'];
  searchModel: SearchModel;
  hiddenFilter?: string;
}

export function useDebouncedFilter({
  runsFilter,
  timeModel,
  searchModel,
  hiddenFilter,
}: DebouncedFilterProps) {
  const timeFilter = useMemo(() => {
    const startDuration = parseRelativeDate(timeModel.duration);
    let startTime = timeModel.start_time || runsFilter?.start_time || undefined;
    let endTime = timeModel.end_time || runsFilter?.end_time || undefined;

    if (timeModel.start_time == null && startDuration != null) {
      startTime = dayjs
        .utc()
        .subtract(startDuration.value, startDuration.unit)
        .toISOString();
      endTime = undefined;
    }

    return { start_time: startTime, end_time: endTime };
  }, [
    runsFilter?.end_time,
    runsFilter?.start_time,
    timeModel.duration,
    timeModel.end_time,
    timeModel.start_time,
  ]);

  // Combine hidden filter with user filter
  const combinedFilter = useMemo(() => {
    if (!hiddenFilter) {
      return searchModel.filter;
    }

    const userFilter = searchModel.filter;
    if (userFilter && userFilter.trim() !== '') {
      return `and(${hiddenFilter}, ${userFilter})`;
    }

    return hiddenFilter;
  }, [searchModel.filter, hiddenFilter]);

  // data fetching
  const fullFilter = useMemo(() => {
    return runsFilter
      ? ({
          ...runsFilter,
          ...timeFilter,
          filter: combinedFilter,
          search_filter: searchModel.searchFilter,
          use_experimental_search: searchModel.useExperimentalSearch,
          tree_filter: searchModel.treeFilter,
          trace_filter: searchModel.traceFilter,
        } satisfies GetRunsQueryParams)
      : null;
  }, [searchModel, runsFilter, timeFilter, combinedFilter]);

  return fullFilter;
}
